/** @jsxImportSource @emotion/react */
import { LoadingOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import Text from '../Text'

type ButtonProps = {
  text: string
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
  variant?: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'danger'
}

const SNButton = ({
  text,
  onClick,
  disabled,
  isLoading,
  variant,
}: ButtonProps) => {
  // primary and quaternary are both colors.PRIMARY fills, so their content
  // follows the tenant-aware pairing.
  const isPrimaryFill = variant === 'primary' || variant === 'quaternary'

  const contentColor = disabled
    ? colors.NEUTRAL_50
    : isPrimaryFill
    ? PRIMARY_BUTTON_CONTENT.text
    : variant === 'tertiary'
    ? colors.PRIMARY
    : colors.WHITE

  return (
    <button
      type='button'
      onClick={onClick}
      css={css`
        height: 40px;
        display: flex;
        padding: 8px 24px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        flex: 1 0 0;
        border-radius: 8px;
        cursor: pointer;
        ${isLoading ||
        (disabled &&
          css`
            cursor: not-allowed;
            opacity: 0.7;
          `)}
        ${variant === 'primary' &&
        css`
          background-color: ${colors.PRIMARY};
          color: ${PRIMARY_BUTTON_CONTENT.text};
        `}
        ${variant === 'secondary' &&
        css`
          background-color: transparent;
          border: 1px solid ${colors.WHITE};
          color: ${colors.WHITE};
        `}
        ${variant === 'tertiary' &&
        css`
          background-color: ${colors.WHITE};
          border: 1px solid ${colors.PRIMARY};
          color: ${colors.PRIMARY};
        `}
        ${variant === 'quaternary' &&
        css`
          background-color: ${colors.PRIMARY};
          border: 1px solid ${colors.PRIMARY};
          color: ${PRIMARY_BUTTON_CONTENT.text};
        `}
        ${variant === 'danger' &&
        css`
          background-color: ${colors.ERROR_200};
          color: ${colors.WHITE};
        `}
        ${disabled &&
        css`
          background-color: ${colors.SECONDARY_200};
        `}
      `}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <LoadingOutlined
          css={css`
            color: ${isPrimaryFill
              ? PRIMARY_BUTTON_CONTENT.spinner
              : colors.WHITE};
            font-size: 16px;
          `}
        />
      ) : (
        <Text variant='heading6' weight='semiBold' color={contentColor}>
          {text}
        </Text>
      )}
    </button>
  )
}

export default SNButton
